import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR_GREY_400, COLOR_PRIMARY_1, COLOR_PRIMARY_2, SIDEBAR_COLOR } from 'src/colors';
import {
  BOOKING_STATUS_COLOR,
  BY_DEFAULT_HIDDEN_COLUMNS_IN_ADVANCED_DASHBOARD_COMPS_TAB,
  COLOR_PRIMARY,
  exchangeRates,
  SCALE_CTA,
} from 'src/const';
import {
  convertAdvancedMarkersToMarkers,
  convertCurrency,
  formatCurrency,
  generateDateList,
  getRateStatus,
  calculatePriceTiers,
  calculateRevenuePotentialRange,
  getPreviousTimePeriod,
  dateDeforeNDays,
  getDaysInMonth,
  isYearMonthKey,
} from 'src/utils/functions';
import {
  getExplorerMarkets,
  getAdvancedMarkers,
  getFutureDailyData,
  updatePriceTiers,
  getMarketMonthlyPerformance,
} from 'src/store/Dashboard/actions';
import { makeStyles } from '@material-ui/core/styles';
import { FilterVal, ListingsProp } from 'src/interfaces/iproptypes';
import ListingModal from './ListingModal';
import { User } from 'src/interfaces/iuser';
import ViewportContainer from 'src/App/Components/Containers/ViewportContainer';

const CustomToolbar = ({ fileName }) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport csvOptions={{ fileName: fileName, utf8WithBom: true }} />
    </GridToolbarContainer>
  );
};

const getKPIPercentageChange = (timePeriod, prevTimePeriod, data, dataKey) => {
  if (!timePeriod || !prevTimePeriod || !data[timePeriod] || !data[prevTimePeriod]) return null;
  const change = parseFloat(
    (
      ((data[timePeriod][dataKey] - data[prevTimePeriod][dataKey]) /
        data[prevTimePeriod][dataKey]) *
      100
    ).toFixed(2)
  );
  return change;
};

const useStyles = makeStyles({
  greyRow: {
    backgroundColor: `${COLOR_GREY_400} !important`,
    '&:hover': {
      backgroundColor: `${COLOR_GREY_400} !important`,
    },
  },
});

const getMarketExplorerData = (markets, listingRange, timePeriod) => {
  let rows: any = [];
  const lowerRangeValue = listingRange[0];
  const upperRangeValue = listingRange[1];
  const prevTimePeriod = getPreviousTimePeriod(timePeriod);
  const prevToPrevTimePeriod = getPreviousTimePeriod(prevTimePeriod);
  let i = 0;
  for (let data of markets) {
    if (
      data[timePeriod]?.total_supply < lowerRangeValue ||
      data[timePeriod]?.total_supply > upperRangeValue
    ) {
      continue;
    }
    rows.push({
      id: i,
      marketName: data['district'],
      marketState: data['city'],
      annualSupply: data[timePeriod]?.total_supply ?? null,
      supplyPercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'total_supply'),
      supplyPercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'total_supply'
      ),
      hostStat1To5: data[timePeriod]?.['1-5_host'] ?? null,
      hostStat6To20: data[timePeriod]?.['6-20_host'] ?? null,
      hostStat20Plus: data[timePeriod]?.['20+_host'] ?? null,
      percStillAvailiableFromLastYear: data[timePeriod]?.yoy_supply_share ?? null,
      AvgOccupancyRate: data[timePeriod]?.average_occupancy_rate ?? null,
      AvgOccupancyRatePercChange: getKPIPercentageChange(
        timePeriod,
        prevTimePeriod,
        data,
        'average_occupancy_rate'
      ),
      AvgOccupancyRatePercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'average_occupancy_rate'
      ),
      adr: data[timePeriod]?.adr ?? null,
      adrPercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'adr'),
      adrPercChangeTwoYears: getKPIPercentageChange(timePeriod, prevToPrevTimePeriod, data, 'adr'),
      revenue: data[timePeriod]?.revenue ?? null,
      revenuePercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'revenue'),
      revenuePercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'revenue'
      ),
    });
    i += 1;
  }

  return rows;
};

const aggregateHostData = (Listings, timePeriod, globalFilteredListingIds, filterActive) => {
  let hostData = {};
  let highestListingCount = 0;
  let lowestListingCount = Number.MAX_SAFE_INTEGER;

  if (Listings) {
    Listings.forEach(data => {
      if (
        !filterActive ||
        (filterActive && globalFilteredListingIds.includes(String(data['listingID'])))
      ) {
        if (!hostData.hasOwnProperty(data['host_id'])) {
          hostData[data['host_id']] = {
            listingCount: 1,
            all_occupancy: [data[`avg_occupancy_rate_${timePeriod}`] || 0],
            all_adr: [data[`adr_${timePeriod}`] || 0],
            all_revenue: [data[`annual_revenue_${timePeriod}`] || 0],
            all_reviews: data['visible_review_count'] || 0,
            all_rating: data['reveiw_scores_rating'] ? [data['reveiw_scores_rating']] : [],
            name: data['host_name'],
          };
        } else {
          hostData[data['host_id']]['listingCount'] += 1;
          hostData[data['host_id']]['all_occupancy'].push(
            data[`avg_occupancy_rate_${timePeriod}`] || 0
          );
          hostData[data['host_id']]['all_adr'].push(data[`adr_${timePeriod}`] || 0);
          hostData[data['host_id']]['all_revenue'].push(data[`annual_revenue_${timePeriod}`] || 0);
          hostData[data['host_id']]['all_reviews'] += data['visible_review_count'] || 0;
          if (data['reveiw_scores_rating'])
            hostData[data['host_id']]['all_rating'].push(data['reveiw_scores_rating']);
        }
      }
    });

    Object.keys(hostData).forEach(host_id => {
      const data = hostData[host_id];
      data['mean_adr'] = parseFloat(
        (data['all_adr'].reduce((a, b) => a + b, 0) / data['all_adr'].length).toFixed(1)
      );
      data['mean_revenue'] = parseFloat(
        (data['all_revenue'].reduce((a, b) => a + b, 0) / data['all_revenue'].length).toFixed(1)
      );
      data['mean_rating'] =
        data['all_rating'].length > 0
          ? parseFloat(
              (data['all_rating'].reduce((a, b) => a + b, 0) / data['all_rating'].length).toFixed(1)
            )
          : null;

      if (data['listingCount'] > highestListingCount) {
        highestListingCount = data['listingCount'];
      }
      if (data['listingCount'] < lowestListingCount) {
        lowestListingCount = data['listingCount'];
      }
    });
  }

  return { hostData, highestListingCount, lowestListingCount };
};

const filterHostData = (hostData, listingRange) => {
  let rows: any = [];
  let i = 0;
  const lowerRangeValue = listingRange[0];
  const upperRangeValue = listingRange[1];

  for (let host_id in hostData) {
    const data = hostData[host_id];
    if (data['listingCount'] >= lowerRangeValue && data['listingCount'] <= upperRangeValue) {
      rows.push({
        id: i,
        hostID: host_id,
        hostName: data['name'],
        noOfListings: data['listingCount'],
        adr: data['mean_adr'],
        revenue: data['mean_revenue'],
        propertyReviews: data['all_reviews'],
        avgPropertyRating: data['mean_rating'],
      });
      i += 1;
    }
  }

  return rows;
};

const getCompsData = (
  allData,
  futureData,
  globalFilteredListingIds,
  filterActive,
  listingId,
  timePeriod,
  currency,
  granualityType,
  dateList,
  months
) => {
  let rows: any = [];
  let i = 0;
  let activeDaysMapping = {
    ltm: 'active_days_ltm',
    '2023': 'active_days_2023',
    '2022': 'active_days_2022',
    '2021': 'active_days_2021',
  };

  let revenueMapping = {
    ltm: 'annual_revenue_ltm',
    '2023': 'annual_revenue_2023',
    '2022': 'annual_revenue_2022',
    '2021': 'annual_revenue_2021',
  };

  let occupancyMapping = {
    ltm: 'avg_occupancy_rate_ltm',
    '2023': 'avg_occupancy_rate_2023',
    '2022': 'avg_occupancy_rate_2022',
    '2021': 'avg_occupancy_rate_2021',
  };

  let dailyRateMapping = {
    ltm: 'adr_ltm',
    '2023': 'adr_2023',
    '2022': 'adr_2022',
    '2021': 'adr_2021',
  };

  for (let data of allData) {
    if (filterActive && !globalFilteredListingIds.includes(String(data['listingID']))) continue;
    let listingFutureData = {} as any;
    Object.values(futureData).forEach((value: any) => {
      if (value['listingID'] == data['listingID']) {
        listingFutureData = value;
      }
    });
    // console.log(">>>>",listingFutureData)
    let listingLevelData = {};
    if (data['listingID'].toString().includes(listingId)) {
      listingLevelData = {
        id: i,
        title: data['name'],
        propertyType: data['room_and_property_type'],
        listingType: data['room_type'],
        lastScrappedDate: data['crawled_on'],
        country: data['country_code'],
        city: data['city'],
        noOfReviews: data['visible_review_count'],
        bedrooms: data['bedrooms'],
        bathrooms: data['bathrooms'],
        maxGuests: data['accommodates'],
        airbnbSuperhost: data['is_superhost'],
        cancellationPolicy: data['cancellation_policy'],
        cleaningFee: data['cleaning_fee'],
        extraPeopleFee: data['extra_fee_person_estimate'],
        checkin: data['checkin'],
        checkout: data['checkout'],
        minimumStay: data['minimum_nights'],
        latitude: data['latitude'],
        longitude: data['longitude'],
        exactLocation: data['is_location_exact'],
        overallRating: data['reveiw_scores_rating'],
        communicationRating: data['communication_rating'],
        accuracyRating: data['accuracy_rating'],
        cleanlinessRating: data['cleanliness_rating'],
        checkinRating: data['checkin_rating'],
        locationRating: data['location_rating'],
        valueRating: data['value_rating'],
        amenities: JSON.stringify(data['amenities']),
        pictureURL: data['thumbnail_url'],
        licence: data['license'],
        listingID: data['listingID'],
        hostID: data['host_id'],
        guestControls: JSON.stringify(data['guest_controls']),
        instantBookable: data['instant_bookable'],
        petsAllowed: `${data['guest_controls']['allows_pets']}`,
        listingURL: `http://airbnb.com/rooms/${data['listingID']}`,
        lastSeen: data['last_seen'],
        hostListingCount: data['hostListingCount'],
        license: data['license'],
        roomType: data['room_type'],
        beds: data['beds'],
        expectations: data['expectations'],
        hostLang: data['host_lang'],
        summary: data['summary'],
        localizedMinimunNightsDescription: data['localized_minimum_nights_description'],
        isBussinessTravelReady: data['is_business_travel_ready'],
        cohostIDs: data['cohost_ids'],
        roomTypeLocalized: data['room_type_localized'],
        cityGuidebook: data['city_guidebook'],
        hostGuidebook: data['host_guidebook'],
        neighbourhoodID: data['neighborhood_id'],
        descriptionLanguage: data['description_language'],
        guestSatisfactionOverall: data['guest_satisfaction_overall'],
        homeTier: data['home_tier'],
        homeCollection: data['home_collection'],
        hostQuotes: data['host_quote'],
        requiredLicence: data['requires_license'],
        supportCleanerLivingWage: data['support_cleaner_living_wage'],
        hostInteraction: data['host_interaction'],
        otherPropertyTypes: data['other_property_types'],
        isHotel: data['is_hotel'],
        accessibilityModule: data['accessibility_module'],
        hasEssentialAmenities: data['has_essentials_amenity'],
        visibility: data['visibility'],
        securityDeposite: data['security_deposit'],
        prices: data['prices'],
        availability_60: data['availability_60'],
        availability_90: data['availability_90'],
        availability_365: data['availability_365'],
        monthlyDiscountRaw: data['monthly_discount_raw'],
        lastReview: data['last_review'],
        firstReview: data['first_review'],
        reviewPerMonth: data['reviews_per_month'],
        calendarAvail: data['calendar_avail'],
        address: data['address'],
        addressType: data['addressType'],
        zipcode: data['zipcode'],
        usMetropolitan: data['us-metropolitan'],
        neighbourhood: data['neighbourhood'],
        hostCountLastUpdated: data['host_count_last_updated'],
        reviewCacheLastUpdatedLTM: data['LTM_review_cache_last_updated'],
        LTMPerformanceLastUpdated: data['ltm_performance_last_updated'],
        avgBookingLeadLTM: data['avg_booking_lead_ltm'],
        avgBookedDailyRateLTM: data['avg_booked_daily_rate_ltm'],
        noOfBookingLTM: data['no_of_bookings_ltm'],
        hostName: data['host_name'],
        currency: data['currency'],
        [`availiableDays_${timePeriod}`]: Math.min(data[activeDaysMapping[timePeriod]], 365),
        [`blockedDays_${timePeriod}`]: Math.max(365 - data[activeDaysMapping[timePeriod]], 0),
        [`occupancyRate_${timePeriod}`]: data[occupancyMapping[timePeriod]],
        [`adr_${timePeriod}`]: data[dailyRateMapping[timePeriod]],
        [`revenue_${timePeriod}`]: data[revenueMapping[timePeriod]],
        [`revenuePotential_${timePeriod}`]: data[revenueMapping[timePeriod]]
          ? Math.round(
              (data[revenueMapping[timePeriod]] / data[activeDaysMapping[timePeriod]]) * 365
            )
          : null,
      };

      {
        granualityType == 'monthly' &&
          months.forEach(month => {
            const daysInMonth = getDaysInMonth(month);
            if (data[month]) {
              let destructData = JSON.parse(data[month]);
              const [adr, activeDays, bookedDays, noOfBookings] = destructData;
              let occupancy: number | null = null;
              let revenue: number | null = null;
              if (activeDays > 0) {
                occupancy = Math.round(Math.min((bookedDays / activeDays) * 100 * 1.2, 100));
                revenue = Math.round(
                  adr * (occupancy / 100) * activeDays +
                    (data['cleaning_fee'] ? noOfBookings * data['cleaning_fee'] : 0)
                );
              }

              listingLevelData[`availiableDays_${month}`] = Math.min(activeDays, daysInMonth);
              listingLevelData[`blockedDays_${month}`] = Math.max(daysInMonth - activeDays, 0);
              listingLevelData[`occupancyRate_${month}`] = occupancy;
              listingLevelData[`adr_${month}`] = adr;
              listingLevelData[`revenue_${month}`] = revenue;
            } else {
              listingLevelData[`availiableDays_${month}`] = 0;
              listingLevelData[`blockedDays_${month}`] = daysInMonth;
              listingLevelData[`occupancyRate_${month}`] = null;
              listingLevelData[`adr_${month}`] = null;
              listingLevelData[`revenue_${month}`] = null;
            }
          });
      }

      {
        granualityType == 'daily' &&
          dateList.map(date => {
            let futureRate = listingFutureData[date];
            const status: 'booked' | 'recentlyBooked' | 'available' | 'inactive' = getRateStatus(
              futureRate
            );
            futureRate = futureRate?.replace(/:b|:rb|:a|:na/g, '');
            futureRate = futureRate?.replace('$', '');
            if (futureRate?.includes('->')) {
              const ratesArr = futureRate?.split(' -> ');
              futureRate = ratesArr[1];
            }
            listingLevelData[`${date}`] = `${futureRate}+${status}`;
            if (futureRate == undefined) {
              listingLevelData[`${date}`] = ` +inactive`;
            }
          });
      }

      rows.push(listingLevelData);
      i += 1; // Increment i within the condition block
    }
  }

  return rows;
};

const HostListingCountFilter = ({
  listingRange,
  setListingRange,
  lowestListingCount,
  highestListingCount,
}) => {
  const listingRangeMarks = [
    {
      value: lowestListingCount,
      label: lowestListingCount,
    },
    {
      value: highestListingCount,
      label: highestListingCount,
    },
  ];

  const ListingRangeChange = (event: Event, newValue: number | number[]) => {
    setListingRange(newValue as number[]);
  };
  return (
    <Grid container item xs={12} md={4} lg={3}>
      <Typography sx={{ fontSize: '12px' }}>Host Listing Count Filter</Typography>
      <Box sx={{ width: '100%' }}>
        <Slider
          size="small"
          value={listingRange}
          min={listingRangeMarks[0]['value']}
          max={listingRangeMarks[1]['value']}
          step={1}
          marks={listingRangeMarks}
          onChange={ListingRangeChange}
          valueLabelDisplay="auto"
          sx={{
            color: COLOR_PRIMARY,
          }}
        />
      </Box>
    </Grid>
  );
};

const CustomCompsFilters = ({
  listingId,
  setListingID,
  timePeriod,
  setTimePeriod,
  granualityType,
  setGranualityType,
  compsRowsData,
}) => {
  const multipleAdvancedListingsCallLoading = useSelector(
    state => (state as any).dashboard.multipleAdvancedListingsCallLoading
  );

  return (
    <Grid container item xs={12}>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Data Granularity</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={granualityType}
            label=""
            onChange={e => {
              setGranualityType(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'daily'}>Daily</MenuItem>
            <MenuItem value={'monthly'}>Monthly</MenuItem>
            <MenuItem value={'yearly'}>Yearly</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Listing ID:</Typography>
        <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={listingId}
            label=""
            onChange={e => {
              setListingID(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            {compsRowsData &&
              compsRowsData.map(data => (
                <MenuItem key={data.listingID} value={data.listingID}>
                  {data.listingID}
                </MenuItem>
              ))}
            <MenuItem key={''} value={''}>
              Show All
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Choose Periods:</Typography>
        <FormControl
          sx={{ margin: '5px 5px 0px 0px', width: '80%' }}
          disabled={granualityType !== 'yearly'}
        >
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={timePeriod}
            label=""
            onChange={e => {
              setTimePeriod(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'ltm'}>LTM</MenuItem>
            <MenuItem value={'2023'}>2023</MenuItem>
            <MenuItem value={'2022'}>2022</MenuItem>
            <MenuItem value={'2021'}>2021</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {multipleAdvancedListingsCallLoading && (
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Grid>
      )}
    </Grid>
  );
};

const OverlayWithCTA = ({ onClick }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 'calc(50% - 50px)',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 3,
    }}
  >
    <Typography
      style={{
        fontSize: '20px',
        fontWeight: 500,
        color: COLOR_PRIMARY_2,
        cursor: 'pointer',
        pointerEvents: 'auto',
        textDecoration: 'underline',
      }}
      onClick={onClick}
    >
      {SCALE_CTA}
    </Typography>
  </Box>
);

const MarketExplorerTab = ({ toggleDrawer, currency, handleOnSampleClick }) => {
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [listingRange, setListingRange] = useState<number[]>([10, 100000]);
  const marketExplorerTableColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'marketName',
      headerName: 'Market Name',
      width: 150,
      editable: false,
    },
    {
      field: 'marketState',
      headerName: 'Region',
      width: 150,
      editable: false,
    },
    {
      field: 'annualSupply',
      headerName: 'Annual Supply',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.annualSupply ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 3000
              : params.value != 'N/A'
              ? formatCurrency(String(params.value))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'supplyPercChange',
      headerName: '1y Supply % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.supplyPercChange != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'supplyPercChangeTwoYears',
      headerName: '2Y Supply % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.supplyPercChange != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'percStillAvailiableFromLastYear',
      headerName: '% still available from last year',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean =
          params.row.percStillAvailiableFromLastYear != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 60
              : params.value && params.value !== 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'hostStat1To5',
      headerName: '1-5 listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat1To5 != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'hostStat6To20',
      headerName: '6-20 listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat6To20 != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'hostStat20Plus',
      headerName: '20+ listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat20Plus != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRate',
      headerName: 'Average Occupancy Rate',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 60 : params.value != 'N/A' ? `${params.value}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRatePercChange',
      headerName: '1Y Avg. Occupancy Rate % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRatePercChangeTwoYears',
      headerName: '2Y Avg. Occupancy Rate % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adr',
      headerName: 'ADR',
      width: 100,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 300
              : params.value != 'N/A'
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adrPercChange',
      headerName: '1Y ADR % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adrPercChangeTwoYears',
      headerName: '2Y ADR % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 100,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 200
              : params.value != 'N/A'
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenuePercChange',
      headerName: '1Y Revenue % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenuePercChangeTwoYears',
      headerName: '2Y Revenue % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue != null ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 30
              : params.value && params.value != 'N/A'
              ? `${params.value.toFixed(2)}%`
              : 'N/A'}
          </Typography>
        );
      },
    },
  ];

  const [marketExplorerRows, setMarketExplorerRows] = useState([]);

  const explorerMarkets = useSelector(state => (state as any).dashboard.explorerMarkets);

  useEffect(() => {
    if (!explorerMarkets.loading && explorerMarkets.data) {
      setMarketExplorerRows(getMarketExplorerData(explorerMarkets.data, listingRange, timePeriod));
    }
  }, [explorerMarkets, listingRange, currency, timePeriod]);

  const [showOverlay, setShowOverlay] = React.useState(true);

  const user: User = useSelector((state: any) => state.dashboard.user);
  useEffect(() => {
    const isDisabled = user.subscriptionPlan != 'scale';
    if (!isDisabled) {
      setShowOverlay(false);
    }
  }, [user]);

  const handleCTAClick = () => {
    handleOnSampleClick('scale');
  };

  return (
    <Grid container item xs={12} sx={{ height: '75vh' }}>
      <Grid container item xs={12} sx={{ height: '100%' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
            {explorerMarkets.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
                <DataGrid
                  sortingOrder={['desc', 'asc']}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiDataGrid-main': {
                      position: 'relative',
                    },
                    '& .MuiDataGrid-overlay': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      filter: showOverlay ? 'blur(4px)' : 'none',
                    },
                  }}
                  rows={marketExplorerRows}
                  columns={marketExplorerTableColumn}
                  disableColumnMenu={true}
                  components={{
                    Toolbar: props => (
                      <CustomToolbar {...props} fileName={`market_explorer_airbtics.csv`} />
                    ),
                  }}
                />
                {/* Overlay and CTA */}
                {showOverlay && <OverlayWithCTA onClick={handleCTAClick} />}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const PropertyManagerTab = ({
  toggleDrawer,
  currency,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
  handleOnSampleClick,
}) => {
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [listingRange, setListingRange] = useState<number[]>([1, 5000]);
  const [propertyManagerRows, setPropertyManagerRows] = useState([]);
  const propertyManagerTableColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true, sortable: true },
    {
      field: 'hostID',
      headerName: 'Host ID',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => {
        const hostID = params.value;
        const url = `https://www.airbnb.com/users/show/${hostID}`;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {hostID}
          </a>
        );
      },
    },
    {
      field: 'hostName',
      headerName: 'Host Name',
      width: 200,
      editable: false,
      sortable: true,
    },
    {
      field: 'noOfListings',
      headerName: 'Number Of Listings',
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'adr',
      headerName: 'Average ADR',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'propertyReviews',
      headerName: 'Property Reviews Count',
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'avgPropertyRating',
      headerName: 'Average Property Rating',
      width: 150,
      editable: false,
      sortable: true,
    },
  ];
  const explorerMarkets = useSelector(state => (state as any).dashboard.explorerMarkets);
  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  const multipleAdvancedListingsCallLoading = useSelector(
    state => (state as any).dashboard.multipleAdvancedListingsCallLoading
  );

  const { hostData, highestListingCount, lowestListingCount } = aggregateHostData(
    advancedListings.data,
    timePeriod,
    globalFilteredListingIds,
    filterActive
  );

  useEffect(() => {
    setListingRange([lowestListingCount, highestListingCount]);
  }, [lowestListingCount, highestListingCount]);

  useEffect(() => {
    if (advancedListings.data) {
      setPropertyManagerRows(filterHostData(hostData, listingRange));
    }
  }, [advancedListings, listingRange]);

  const [showOverlay, setShowOverlay] = React.useState(true);
  const user: User = useSelector((state: any) => state.dashboard.user);

  useEffect(() => {
    const isDisabled = user.subscriptionPlan != 'scale';
    if (!isDisabled) {
      setShowOverlay(false);
    }
  }, [user]);

  const handleCTAClick = () => {
    handleOnSampleClick('scale');
  };

  return (
    <Grid container item xs={12} sx={{ height: '75vh' }}>
      {advancedListings.data && multipleAdvancedListingsCallLoading && (
        <Alert severity="info" sx={{ textAlign: 'center', width: '100%', mb: 2 }}>
          We are still fetching data for the selected market. This may take a few minutes.
        </Alert>
      )}

      {advancedListings.data && !advancedListings.loading && (
        <HostListingCountFilter
          listingRange={listingRange}
          setListingRange={setListingRange}
          lowestListingCount={lowestListingCount}
          highestListingCount={highestListingCount}
        />
      )}
      <Grid container item xs={12} sx={{ height: '100%' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          {explorerMarkets.data?.length > 0 && (
            <Box sx={{ height: '100%', width: '100%' }}>
              {!advancedListings.data && !advancedListings.loading ? (
                <Alert severity="info" sx={{ textAlign: 'center' }}>
                  Select a market from the dropdown to view data
                </Alert>
              ) : advancedListings.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress />
                </div>
              ) : (
                <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
                  <DataGrid
                    sortingOrder={['desc', 'asc']}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiDataGrid-main': {
                        position: 'relative',
                      },
                      '& .MuiDataGrid-overlay': {
                        display: 'none',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        filter: showOverlay ? 'blur(4px)' : 'none',
                      },
                    }}
                    rows={propertyManagerRows}
                    columns={propertyManagerTableColumn}
                    disableColumnFilter={true}
                    disableColumnMenu={true}
                    components={{
                      Toolbar: props => (
                        <CustomToolbar
                          {...props}
                          fileName={`${selectedMarket.district}_Property_Manager_Airbtics.csv`}
                        />
                      ),
                    }}
                  />
                  {/* Overlay and CTA */}
                  {showOverlay && <OverlayWithCTA onClick={handleCTAClick} />}
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const CustomRenderCellForComps = ({ params }) => {
  let lastSeen = new Date(params?.row?.lastSeen);
  const dateBefore14Days = dateDeforeNDays(14);

  if (lastSeen >= dateBefore14Days) {
    return (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2">{params['value']}</Typography>
      </Box>
    );
  } else {
    return (
      <Tooltip
        title={`This listing was last observed by our system on ${params?.row?.lastSeen}, so this may not be on Airbnb anymore`}
        arrow
      >
        <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      </Tooltip>
    );
  }
};

const getCompsTableColumns = (granualityType, timePeriod, currency, dateList, months) => {
  let compsTableColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'listingID',
      headerName: 'Airbnb Listing ID',
      width: 200,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'title',
      headerName: 'Listing Title',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'propertyType',
      headerName: 'Property Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'listingType',
      headerName: 'Listing Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'lastScrappedDate',
      headerName: 'Last Scrapped Date',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'noOfReviews',
      headerName: 'Number Of Reviews',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'bathrooms',
      headerName: 'Bathrooms',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'maxGuests',
      headerName: 'Max Guests',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'airbnbSuperhost',
      headerName: 'Airbnb Superhost',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cancellationPolicy',
      headerName: 'Cancellation Policy',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cleaningFee',
      headerName: 'Cleaning Fee (USD)',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'extraPeopleFee',
      headerName: 'Extra People Fee',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'checkin',
      headerName: 'Check-in Time',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'checkout',
      headerName: 'Check-out Time',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'minimumStay',
      headerName: 'Minimum Stay',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'exactLocation',
      headerName: 'Exact Location',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'overallRating',
      headerName: 'Overall Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'accuracyRating',
      headerName: 'Airbnb Accuracy Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cleanlinessRating',
      headerName: 'Airbnb Cleanliness Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'checkinRating',
      headerName: 'Airbnb Checkin Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'locationRating',
      headerName: 'Airbnb Location Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'valueRating',
      headerName: 'Airbnb Value Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'amenities',
      headerName: 'Amenities',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'pictureURL',
      headerName: 'Picture URL',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostID',
      headerName: 'Airbnb Host ID',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'guestControls',
      headerName: 'Guest Controls',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'instantBookable',
      headerName: 'Instant Bookable',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'petsAllowed',
      headerName: 'Pets Allowed',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'listingURL',
      headerName: 'Listing URL',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostListingCount',
      headerName: 'Host Listing Count',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'communicationRating',
      headerName: 'Communication Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'license',
      headerName: 'Licence',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'roomType',
      headerName: 'Room Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'beds',
      headerName: 'Number of beds',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'expectations',
      headerName: 'Expectations',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostLang',
      headerName: 'Host Lang',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'summary',
      headerName: 'Summary',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'localizedMinimunNightsDescription',
      headerName: 'Localized Minimun Nights Description',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'isBussinessTravelReady',
      headerName: 'Bussiness Travel Ready',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cohostIDs',
      headerName: 'Cohost IDs',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'roomTypeLocalized',
      headerName: 'Room Type Localized',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cityGuidebook',
      headerName: 'City Guide Book',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostGuidebook',
      headerName: 'Host Guide Book',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'neighbourhoodID',
      headerName: 'NeighbourhoodID',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'descriptionLanguage',
      headerName: 'Description Language',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'guestSatisfactionOverall',
      headerName: 'Guest Satisfaction Overall',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'homeTier',
      headerName: 'Home Tier',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'homeCollection',
      headerName: 'Home Collection',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostQuotes',
      headerName: 'Host Quotes',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'requiredLicence',
      headerName: 'Licence Required',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'supportCleanerLivingWage',
      headerName: 'Support Cleaner Living Wage',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostInteraction',
      headerName: 'Host Interaction',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'otherPropertyTypes',
      headerName: 'Other Property Types',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'isHotel',
      headerName: 'Hotel',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'accessibilityModule',
      headerName: 'Accessibility Module',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hasEssentialAmenities',
      headerName: 'Has Essentials Amenities',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'visibility',
      headerName: 'Visibility',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'securityDeposite',
      headerName: 'Security Deposite',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'prices',
      headerName: 'Prices',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'availability_60',
      headerName: 'Availiability 60',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'availability_90',
      headerName: 'Availiability 90',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'availability_365',
      headerName: 'Availability 365',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'monthlyDiscountRaw',
      headerName: 'Monthly Raw Discount',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'lastReview',
      headerName: 'Last Review',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'firstReview',
      headerName: 'First Review',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'reviewPerMonth',
      headerName: 'Review Per Month',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'calendarAvail',
      headerName: 'Calendar Availiable',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'addressType',
      headerName: 'Address Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'zipcode',
      headerName: 'Zipcode',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'usMetropolitan',
      headerName: 'US Metropolitan',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'neighbourhood',
      headerName: 'Neighbourhood',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostCountLastUpdated',
      headerName: 'Host Count Last Updated',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'reviewCacheLastUpdatedLTM',
      headerName: 'Review Cache Last Updated LTM',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'LTMPerformanceLastUpdated',
      headerName: 'LTM Performance Last Updated',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'avgBookingLeadLTM',
      headerName: 'Avg Booking Lead LTM',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'avgBookedDailyRateLTM',
      headerName: 'Avg Booked Daily Rate LTM',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'noOfBookingLTM',
      headerName: 'No Of Bookings LTM',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostName',
      headerName: 'Host Name',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
  ];

  let years = ['ltm', '2023', '2022', '2021'];

  if (granualityType == 'daily') {
    dateList.forEach(date => {
      compsTableColumns.push({
        field: `${date}`,
        headerName: `${date}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => {
          if (params.value) {
            const [value, status] = params.value.split('+');
            return (
              <Box
                sx={{
                  backgroundColor: BOOKING_STATUS_COLOR[status],
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: SIDEBAR_COLOR, fontWeight: 400 }}>
                  {exchangeRates[currency].symbol}
                  {formatCurrency(String(convertCurrency(currency, value)))}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: SIDEBAR_COLOR, fontWeight: 400 }}>N/A</Typography>
              </Box>
            );
          }
        },
        sortComparator: (v1, v2) => {
          let value1 = v1 ? parseFloat(v1.split('+')[0]) : 0;
          value1 = isNaN(value1) ? 0 : value1;
          let value2 = v2 ? parseFloat(v2.split('+')[0]) : 0;
          value2 = isNaN(value2) ? 0 : value2;
          return value1 - value2;
        },
      });
    });
  }

  if (granualityType == 'monthly') {
    months.forEach(month => {
      compsTableColumns.push({
        field: `availiableDays_${month}`,
        headerName: `Count Availiable Days ${month}`,
        width: 150,
        editable: false,
      });
      compsTableColumns.push({
        field: `blockedDays_${month}`,
        headerName: `Count Blocked Days ${month}`,
        width: 150,
        editable: false,
      });
      compsTableColumns.push({
        field: `occupancyRate_${month}`,
        headerName: `Occupancy Rate ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value ? params.value : 'inactive'}
            {params.value && '%'}
          </Typography>
        ),
      });
      compsTableColumns.push({
        field: `adr_${month}`,
        headerName: `Average Daily Rate ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value && exchangeRates[currency].symbol}
            {params.value
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'inactive'}
          </Typography>
        ),
      });
      compsTableColumns.push({
        field: `revenue_${month}`,
        headerName: `Revenue ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value && exchangeRates[currency].symbol}
            {params.value
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'inactive'}
          </Typography>
        ),
      });
    });
  }

  if (granualityType === 'yearly') {
    years.forEach(year => {
      if (timePeriod === year) {
        compsTableColumns.push({
          field: `availiableDays_${year}`,
          headerName: `Count Availiable Days ${year}`,
          width: 150,
          editable: false,
        });
        compsTableColumns.push({
          field: `blockedDays_${year}`,
          headerName: `Count Blocked Days ${year}`,
          width: 150,
          editable: false,
        });
        compsTableColumns.push({
          field: `occupancyRate_${year}`,
          headerName: `Occupancy Rate ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value ? params.value : 'inactive'}
              {params.value && '%'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `adr_${year}`,
          headerName: `Average Daily Rate ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `revenue_${year}`,
          headerName: `Revenue ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `revenuePotential_${year}`,
          headerName: `Revenue Potential ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
      }
    });
  }

  return compsTableColumns;
};

const CompsTab = ({
  toggleDrawer,
  currency,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
}) => {
  const [listingId, setListingID] = useState('');
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [granualityType, setGranualityType] = useState('yearly');
  const [openListingModal, setOpenListingModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const startDate = '2024-05-30';
  const days = 365;
  const dateList: string[] = generateDateList(startDate, days);
  // const compsets: any = getCompetitorRates(compRatesObj, dateList, compMetadata);

  const [compsRowsData, setCompsRowsData] = useState([]);
  const [compsTabColumn, setCompsTabColumn] = useState<GridColDef<any, any, any>[]>([]);
  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  const listingsFutureData = useSelector(state => (state as any).dashboard.listingsFutureData);

  useEffect(() => {
    if (
      !advancedListings.loading &&
      advancedListings.data &&
      !listingsFutureData.loading &&
      listingsFutureData.data
    ) {
      const months = Object.keys(advancedListings.data[0]).filter(isYearMonthKey);
      setCompsTabColumn(
        getCompsTableColumns(granualityType, timePeriod, currency, dateList, months)
      );
      setCompsRowsData(
        getCompsData(
          advancedListings.data,
          listingsFutureData.data,
          globalFilteredListingIds,
          filterActive,
          listingId,
          timePeriod,
          currency,
          granualityType,
          dateList,
          months
        )
      );
    }
  }, [
    advancedListings,
    listingsFutureData,
    listingId,
    timePeriod,
    currency,
    granualityType,
    globalFilteredListingIds,
    filterActive,
  ]);

  const classes = useStyles();

  const handleRowClick = params => {
    setSelectedRow(params.row);
    setOpenListingModal(true);
  };

  const handleClose = () => {
    setOpenListingModal(false);
    setSelectedRow(null);
  };

  const getRowClassName = params => {
    let lastSeen = new Date(params?.row?.lastSeen);
    const dateBefore14Days = dateDeforeNDays(14);

    if (lastSeen >= dateBefore14Days) {
      return '';
    } else {
      return classes.greyRow;
    }
  };

  return (
    <Grid container item xs={12} sx={{ height: '80vh' }}>
      <Grid item xs={12} sx={{ height: '100px' }}>
        <CustomCompsFilters
          listingId={listingId}
          setListingID={setListingID}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          granualityType={granualityType}
          setGranualityType={setGranualityType}
          compsRowsData={advancedListings.data}
        />
      </Grid>
      <Grid container item xs={12} sx={{ height: 'calc(100% - 100px)' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {advancedListings.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                sortingOrder={['desc', 'asc']}
                sx={{ backgroundColor: 'white', '.MuiDataGrid-row': { cursor: 'pointer' } }}
                rows={compsRowsData}
                columns={compsTabColumn}
                disableColumnMenu={true}
                onRowClick={handleRowClick}
                getRowClassName={getRowClassName}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'lastScrappedDate', sort: 'desc' }],
                  },
                  columns: {
                    columnVisibilityModel: BY_DEFAULT_HIDDEN_COLUMNS_IN_ADVANCED_DASHBOARD_COMPS_TAB,
                  },
                }}
                components={{
                  Toolbar: props => (
                    <CustomToolbar
                      {...props}
                      fileName={`${selectedMarket.district}_Comps_Airbtics.csv`}
                    />
                  ),
                }}
              />
            )}
            <ListingModal
              open={openListingModal}
              onClose={handleClose}
              rowData={selectedRow}
              advancedListings={advancedListings}
              listingsFutureData={listingsFutureData}
              currency={currency}
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const CustomTabPanel = ({
  value,
  index,
  toggleDrawer,
  currency,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
  handleOnSampleClick,
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {value == 0 && (
            <MarketExplorerTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              handleOnSampleClick={handleOnSampleClick}
            />
          )}
          {value == 1 && (
            <PropertyManagerTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              globalFilteredListingIds={globalFilteredListingIds}
              filterActive={filterActive}
              selectedMarket={selectedMarket}
              handleOnSampleClick={handleOnSampleClick}
            />
          )}
          {/* {value == 2 && (
            <CompsTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              globalFilteredListingIds={globalFilteredListingIds}
              filterActive={filterActive}
              selectedMarket={selectedMarket}
            />
          )} */}
        </Box>
      )}
    </Box>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const AdvancedDashboard = ({
  matches,
  user,
  advancedTab,
  setAdvancedTab,
  dummyMarket,
  handleOnSampleClick,
  isLandingView,
}: {
  matches: boolean;
  user: any;
  advancedTab: number;
  setAdvancedTab: any;
  dummyMarket: any;
  handleOnSampleClick: (reqSubscriptionPlan?: 'pro' | 'expert' | 'scale') => void;
  isLandingView?: boolean;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = location.search;
  const tabQuery = new URLSearchParams(searchParams).get('tab');

  const [selectedMarket, setSelectedMarket] = useState<null | {
    district: string;
    city: string;
    countryCode: string;
  }>(null);
  const explorerMarkets = useSelector(state => (state as any).dashboard.explorerMarkets);
  const currency = useSelector(state => (state as any).dashboard.currency);
  const isDisabled = user.subscriptionPlan != 'scale';
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterActive, setFilterActive] = useState(false);
  const [globalFilteredListingIds, setGlobalFilteredListingIds] = useState<string[]>([]);

  useEffect(() => {
    if (user.subscriptionPlan === 'scale') {
      dispatch(getExplorerMarkets('saved'));
    } else {
      dispatch(getExplorerMarkets('GB'));
    }
  }, [user]);

  const toggleDrawer = () => {
    setFilterOpen(!filterOpen);
  };

  useEffect(() => {
    if (tabQuery && ['0', '1', '2'].includes(tabQuery)) {
      setAdvancedTab(Number(tabQuery));
    }
  }, []);

  useEffect(() => {
    if (selectedMarket) {
      dispatch(
        getAdvancedMarkers(selectedMarket.district, selectedMarket.city, selectedMarket.countryCode)
      );
      dispatch(
        getFutureDailyData(selectedMarket.district, selectedMarket.city, selectedMarket.countryCode)
      );
    }
  }, [selectedMarket]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setAdvancedTab(newValue);
  };

  const marketSelector = (
    <FormControl
      sx={{
        width: matches ? '100%' : 'auto',
        minWidth: matches ? '100%' : 120,
      }}
      size="small"
      variant="standard"
    >
      <InputLabel>Market</InputLabel>
      <Select
        value={selectedMarket ? JSON.stringify(selectedMarket) : null}
        label="Market"
        onChange={e => {
          setSelectedMarket(JSON.parse(e.target.value as string));
        }}
      >
        {explorerMarkets.data.map(
          (market: { district: string; city: string; country_code: string }) => (
            <MenuItem
              key={market.district}
              value={JSON.stringify({
                district: market.district,
                city: market.city,
                countryCode: market.country_code,
              })}
            >
              {market.district}
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );

  return (
    <>
      <ViewportContainer>
        <Box sx={{ width: '100%' }}>
          {!isLandingView && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
                <Grid item xs={12} lg={6} md={6}>
                  <Tabs value={advancedTab} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="Market Explorer" {...a11yProps(0)} />
                    <Tab label="Property Manager" {...a11yProps(1)} />
                    {user.isAdvancePlanUser && <Tab label="Comps" {...a11yProps(2)} />}
                  </Tabs>
                </Grid>

                {!matches && (
                  <Grid
                    container
                    direction="row"
                    item
                    xs={12}
                    lg={6}
                    md={6}
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={2}
                  >
                    {advancedTab != 0 &&
                      !explorerMarkets.loading &&
                      explorerMarkets.data?.length > 0 && <Grid item>{marketSelector}</Grid>}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {matches && !isLandingView && (
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              gap={2}
              sx={{ mt: 2 }}
            >
              {advancedTab != 0 && !explorerMarkets.loading && explorerMarkets.data?.length > 0 && (
                <Grid item xs={12} sx={{ width: '100%' }}>
                  {marketSelector}
                </Grid>
              )}
            </Grid>
          )}

          {!explorerMarkets.loading && explorerMarkets.data?.length == 0 && (
            <Grid item xs={12} sx={{ width: '100%', mt: 2 }}>
              <Alert severity="info">
                You haven’t saved a market yet. Save a market to compare or download its summary
                below.{' '}
                <a
                  href="https://youtu.be/JGJFRhfbmu4"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}
                >
                  Learn how to save a market
                </a>{' '}
                and get started!
              </Alert>
            </Grid>
          )}

          <CustomTabPanel
            value={advancedTab}
            index={0}
            toggleDrawer={toggleDrawer}
            currency={currency}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedMarket={selectedMarket}
            handleOnSampleClick={handleOnSampleClick}
          />
          <CustomTabPanel
            value={advancedTab}
            index={1}
            toggleDrawer={toggleDrawer}
            currency={currency}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedMarket={selectedMarket}
            handleOnSampleClick={handleOnSampleClick}
          />
          {/* <CustomTabPanel
            value={advancedTab}
            index={2}
            toggleDrawer={toggleDrawer}
            currency={currency}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedCountry={selectedCountry}
            selectedMarket={selectedMarket}
            handleOnSampleClick={handleOnSampleClick}
          /> */}
        </Box>
      </ViewportContainer>
    </>
  );
};

export default AdvancedDashboard;
